/**
 * @description Pure React modal for resending external user emails
 * Uses React SmartSelect component and React Modal component
 */
import React, { useState } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { SmartSelect } from '@Shared.Angular/flowingly.components/smartselect/SmartSelect/SmartSelect';
import { getService } from '@Client/runner.hooks/useService';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import IEmailRecipient from '@Shared.Angular/@types/core/contracts/queryModel/card/emailRecipient';
import INodeCustomEmailCommandData from '@Shared.Angular/@types/core/contracts/commands/modeler/nodeCustomEmailCommandData';
import { Guid } from '@Shared.Angular/@types/guid';

type Props = {
  stepId: Guid;
  flowId: Guid;
  nodeCustomMail: INodeCustomEmailCommandData;
  onClose: (result?: { selectedEmailRecipients: IEmailRecipient[] }) => void;
};

const ResendEmailModal: React.FC<Props> = ({
  stepId,
  flowId,
  nodeCustomMail,
  onClose
}) => {
  const flowApiService = getService('flowApiService');
  const notificationService = getService('notificationService');

  const [selectedRecipients, setSelectedRecipients] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRecipientsChange = (recipients: IFlowinglySearchResult[]) => {
    setSelectedRecipients(recipients);
  };

  const validateAndSendEmail = () => {
    if (selectedRecipients.length === 0) {
      notificationService.showErrorToast(
        'Please enter at least one email address.'
      );
      return;
    }

    setIsSubmitting(true);

    // Convert IFlowinglySearchResult[] to IEmailRecipient[]
    // Both use camelCase (id, displayName, searchEntityType)
    const emailRecipients: IEmailRecipient[] = selectedRecipients.map(
      (recipient) => ({
        id: recipient.id,
        fieldModelerNodeId: undefined,
        fieldName: '',
        searchEntityType: recipient.searchEntityType || '',
        displayName: recipient.displayName
      })
    );

    // Update the NodeCustomMail with the new recipients
    const updatedNodeCustomMail: INodeCustomEmailCommandData = {
      ...nodeCustomMail,
      selectedEmailRecipients: emailRecipients
    };

    flowApiService
      .resendExternalUserEmail(updatedNodeCustomMail, stepId, flowId)
      .then(() => {
        notificationService.showSuccessToast(
          'External User Email Resent Successfully'
        );
        onClose({ selectedEmailRecipients: emailRecipients });
      })
      .catch((error: unknown) => {
        notificationService.showErrorToast(
          'Failed to resend email. Please try again.'
        );
        console.error('Error resending email:', error);
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      id="resend-email-modal"
      title="Recipient List"
      onClose={onClose}
      onCancelClick={onClose}
      cancelButtonText="Cancel"
      onActionClick={validateAndSendEmail}
      actionButtonText="Resend"
      isActionDisabled={selectedRecipients.length === 0 || isSubmitting}
      actionButtonType="primary"
      size="md"
    >
      <SmartSelect
        id="resend-email-smart-select"
        initialSelectedItems={[]}
        onChange={handleRecipientsChange}
        headerText="Recipient List"
        infoText="People who will receive this email."
        showCustomEmailInput={true}
        showUser={false}
        showTeam={false}
      />
    </Modal>
  );
};

export default ResendEmailModal;
